import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import QRCodeStyling from "qr-code-styling";
import { DesktopDialogOverlay } from "../app/DesktopDialogOverlay";

// Rendered sizes in CSS pixels, matched by the boxes below (size-16, size-72).
const THUMBNAIL_PX = 64;
const ENLARGED_PX = 288;

/**
 * A QR code of a page's own URL, for the desktop reader who will walk the
 * congress with their phone. The rail shows it small; a click enlarges it in a
 * dialog, at a size a phone camera reads from across a desk.
 *
 * UI machinery: the library draws into a node it owns, so this keeps the ref,
 * and whether the dialog is open is this component's own state.
 */
export function DesktopCongressQrCode({
  url,
  label,
}: {
  url: string;
  /** Accessible name of the code */
  label: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"closed" | "open">("closed");
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setDialog("open");
        }}
        aria-label={t("Enlarge the QR code")}
        className="shrink-0 cursor-zoom-in rounded-lg border border-ds-slate-100 bg-ds-white p-1 hover:border-ds-slate-300"
      >
        <QrCodeImage
          url={url}
          pixels={THUMBNAIL_PX}
          label={label}
          className="size-16"
        />
      </button>
      {dialog === "open" ? (
        <DesktopDialogOverlay
          label={label}
          onClose={() => {
            setDialog("closed");
          }}
          cardClassName="relative flex flex-col items-center gap-4 rounded-2xl bg-ds-white p-8 font-dm-sans"
        >
          <button
            type="button"
            onClick={() => {
              setDialog("closed");
            }}
            aria-label={t("Close")}
            className="absolute top-3 right-3 flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-slate-700 hover:bg-ds-slate-100"
          >
            <IconX className="size-5" stroke={1.5} />
          </button>
          <QrCodeImage
            url={url}
            pixels={ENLARGED_PX}
            label={label}
            className="size-72"
          />
          <p className="m-0 text-ui-body text-ds-slate-700">
            {t("Scan to open this page.")}
          </p>
        </DesktopDialogOverlay>
      ) : null}
    </>
  );
}

function QrCodeImage({
  url,
  pixels,
  label,
  className,
}: {
  url: string;
  pixels: number;
  label: string;
  className: string;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null) throw new Error("should not happen: QR node unmounted");
    const qrCode = new QRCodeStyling({
      data: url,
      width: pixels,
      height: pixels,
      type: "svg",
      margin: 0,
    });
    qrCode.append(node);
    return () => {
      node.replaceChildren();
    };
  }, [url, pixels]);
  return <div ref={ref} role="img" aria-label={label} className={className} />;
}
